import TextBlockEdit from '@/modules/content-blocks/components/forms/text/TextBlockEdit.vue';
import TextBlockContent from '@/modules/content-blocks/components/views/TextContent.vue';
import * as TextBlockSchema from '@/modules/content-blocks/components/forms/text/textBlock.schema';
import AboutBlockEdit from '@/modules/content-blocks/components/forms/about/AboutBlockEdit.vue';
import AboutBlockContent from '@/modules/content-blocks/components/views/AboutContent.vue';
import * as AboutBlockSchema from '@/modules/content-blocks/components/forms/about/aboutBlock.schema';
import HeaderBlockEdit from '@/modules/content-blocks/components/forms/header/HeaderBlockEdit.vue';
import HeaderBlockContent from '@/modules/content-blocks/components/views/HeaderContent.vue';
import * as HeaderBlockSchema from '@/modules/content-blocks/components/forms/header/headerBlock.schema';
import ImageBlockEdit from '@/modules/content-blocks/components/forms/image/ImageBlockEdit.vue';
import ImageBlockContent from '@/modules/content-blocks/components/views/ImageContent.vue';
import * as ImageBlockSchema from '@/modules/content-blocks/components/forms/image/imageBlock.schema';
import VideoBlockEdit from '@/modules/content-blocks/components/forms/video/VideoBlockEdit.vue';
import VideoBlockContent from '@/modules/content-blocks/components/views/VideoContent.vue';
import * as VideoBlockSchema from '@/modules/content-blocks/components/forms/video/videoBlock.schema';
import ContactsBlockEdit from '@/modules/content-blocks/components/forms/contacts/ContactsBlockEdit.vue';
import ContactsBlockContent from '@/modules/content-blocks/components/views/ContactsContent.vue';
import * as ContactsBlockSchema from '@/modules/content-blocks/components/forms/contacts/contactsBlock.schema';
import CarouselBlockEdit from '@/modules/content-blocks/components/forms/carousel/CarouselBlockEdit.vue';
import CarouselBlockContent from '@/modules/content-blocks/components/views/CarouselContent.vue';
import * as CarouselBlockSchema from '@/modules/content-blocks/components/forms/carousel/carouselBlock.schema';
import JobsBlockEdit from '@/modules/content-blocks/components/forms/jobs/JobsBlockEdit.vue';
import JobsBlockContent from '@/modules/content-blocks/components/views/JobsContent.vue';
import * as JobsBlockSchema from '@/modules/content-blocks/components/forms/jobs/jobsBlock.schema';
import LocationBlockEdit from '@/modules/content-blocks/components/forms/location/LocationBlockEdit.vue';
import LocationBlockContent from '@/modules/content-blocks/components/views/LocationContent.vue';
import * as LocationBlockSchema from '@/modules/content-blocks/components/forms/location/locationBlock.schema';

export interface ContentBlockDefinition {
   id: string;
   isRemovable: boolean;
   isAddable: boolean;
   isDraggable: boolean;
   defaultData?: any;
   schema: () => any;
   i18n: {
      name: string;
      description?: string;
      image?: string;
   };
   components: {
      select?: Component;
      preview?: Component;
      edit: Component;
      content: Component;
   };
}

enum ContentBlockOptions {
   TEXT = 'text',
   HEADER = 'header',
   IMAGE = 'image',
   CONTACT = 'contact',
   ABOUT = 'about',
   CAROUSEL = 'carousel',
   JOB_BOARD = 'job_board',
   LOCATION = 'location',
   VIDEO = 'video',
   FOOTER = 'footer',
}

const definitions: Record<string, ContentBlockDefinition> = {
   text: {
      id: ContentBlockOptions.TEXT,
      i18n: {
         name: 'live-editor.content-blocks.text.i18n.name',
         description: 'live-editor.content-blocks.text.i18n.description',
         image: 'live-editor.content-blocks.text.i18n.image',
      },
      isRemovable: true,
      isAddable: true,
      isDraggable: true,
      defaultData: { text: {} },
      schema: TextBlockSchema.getRules,
      components: {
         edit: TextBlockEdit,
         content: TextBlockContent,
      },
   },
   header: {
      id: ContentBlockOptions.HEADER,
      i18n: {
         name: 'live-editor.content-blocks.header.i18n.name',
         description: 'live-editor.content-blocks.header.i18n.description',
         image: 'live-editor.content-blocks.header.i18n.image',
      },
      isRemovable: false,
      isAddable: false,
      isDraggable: true,
      schema: HeaderBlockSchema.getRules,
      components: {
         edit: HeaderBlockEdit,
         content: HeaderBlockContent,
      },
   },
   image: {
      id: ContentBlockOptions.IMAGE,
      i18n: {
         name: 'live-editor.content-blocks.image.i18n.name',
         description: 'live-editor.content-blocks.image.i18n.description',
         image: 'live-editor.content-blocks.image.i18n.image',
      },
      isRemovable: true,
      isAddable: true,
      isDraggable: true,
      defaultData: { image: {} },
      schema: ImageBlockSchema.getRules,
      components: {
         edit: ImageBlockEdit,
         content: ImageBlockContent,
      },
   },
   contact: {
      id: ContentBlockOptions.CONTACT,
      i18n: {
         name: 'live-editor.content-blocks.contacts.i18n.name',
         description: 'live-editor.content-blocks.contacts.i18n.description',
         image: 'live-editor.content-blocks.contacts.i18n.image',
      },
      isRemovable: true,
      isAddable: true,
      isDraggable: true,
      defaultData: { contacts: [] },
      schema: ContactsBlockSchema.getRules,
      components: {
         edit: ContactsBlockEdit,
         content: ContactsBlockContent,
      },
   },
   about: {
      id: ContentBlockOptions.ABOUT,
      i18n: {
         name: 'live-editor.content-blocks.about.i18n.name',
         description: 'live-editor.content-blocks.about.i18n.description',
         image: 'live-editor.content-blocks.about.i18n.image',
      },
      isRemovable: true,
      isAddable: true,
      isDraggable: true,
      defaultData: { rows: [] },
      schema: AboutBlockSchema.getRules,
      components: {
         edit: AboutBlockEdit,
         content: AboutBlockContent,
      },
   },
   carousel: {
      id: ContentBlockOptions.CAROUSEL,
      i18n: {
         name: 'live-editor.content-blocks.carousel.i18n.name',
         description: 'live-editor.content-blocks.carousel.i18n.description',
         image: 'live-editor.content-blocks.carousel.i18n.image',
      },
      isRemovable: true,
      isAddable: true,
      isDraggable: true,
      defaultData: { slides: [] },
      schema: CarouselBlockSchema.getRules,
      components: {
         edit: CarouselBlockEdit,
         content: CarouselBlockContent,
      },
   },
   job_board: {
      id: ContentBlockOptions.JOB_BOARD,
      i18n: {
         name: 'live-editor.content-blocks.job_board.i18n.name',
         description: 'live-editor.content-blocks.job_board.i18n.description',
         image: 'live-editor.content-blocks.job_board.i18n.image',
      },
      isRemovable: false,
      isAddable: false,
      isDraggable: true,
      schema: JobsBlockSchema.getRules,
      components: {
         edit: JobsBlockEdit,
         content: JobsBlockContent,
      },
   },
   location: {
      id: ContentBlockOptions.LOCATION,
      i18n: {
         name: 'live-editor.content-blocks.location.i18n.name',
         description: 'live-editor.content-blocks.location.i18n.description',
         image: 'live-editor.content-blocks.location.i18n.image',
      },
      isRemovable: true,
      isAddable: true,
      isDraggable: true,
      defaultData: { locations: [] },
      schema: LocationBlockSchema.getRules,
      components: {
         edit: LocationBlockEdit,
         content: LocationBlockContent,
      },
   },
   video: {
      id: ContentBlockOptions.VIDEO,
      i18n: {
         name: 'live-editor.content-blocks.video.i18n.name',
         description: 'live-editor.content-blocks.video.i18n.description',
         image: 'live-editor.content-blocks.video.i18n.image',
      },
      isRemovable: true,
      isAddable: true,
      isDraggable: true,
      defaultData: { video: {}, video_type: 'upload' },
      schema: VideoBlockSchema.getRules,
      components: {
         edit: VideoBlockEdit,
         content: VideoBlockContent,
      },
   },
};

export { ContentBlockOptions };
export const getBlock = (id: string): ContentBlockDefinition | undefined => definitions[id];
export const listSelectableBlocks = (): ContentBlockDefinition[] => Object.values(definitions).filter((def) => !!def.isAddable);
